// The register spreadsheet (pilot slice 6, ADR-0031): one layout for the import template, the import
// itself and the export, so a committee can fill, check and share the same sheet.
import type { Role } from './auth';

/** Who may download the register as a spreadsheet: the committee, never ward-scoped roles (personal data). */
export const EXPORT_ROLES: readonly Role[] = ['MAHALLU_ADMIN', 'PRESIDENT', 'SECRETARY', 'TREASURER'];

export const XLSX_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

export const IMPORT_LIMITS = {
  /** Upload size; a mahallu of 2,000 houses fits in well under 1 MB. */
  bytes: 2 * 1024 * 1024,
  /** The same file unpacked; anything larger is refused before it is read (zip bombs). */
  unpackedBytes: 40 * 1024 * 1024,
  rows: 10000,
  /** Problems listed back; the count is always complete. */
  listedProblems: 200,
} as const;

export interface SheetColumn {
  key: string;
  en: string;
  ml: string;
  /** Shown in the template's instructions sheet. */
  helpEn: string;
  helpMl: string;
}

/** Columns in order. A row is one member; rows with the same house key form one house. */
export const REGISTER_COLUMNS = [
  { key: 'houseKey', en: 'House key', ml: 'വീട് കീ', helpEn: 'Required. Any mark that groups the rows of one house, e.g. the number in your register book. Rows with the same key are one house.', helpMl: 'നിർബന്ധം. ഒരു വീട്ടിലെ വരികളെ ഒന്നിച്ചാക്കുന്ന അടയാളം, ഉദാ. രജിസ്റ്റർ ബുക്കിലെ നമ്പർ. ഒരേ കീ ഉള്ള വരികൾ ഒരു വീടാണ്.' },
  { key: 'houseNo', en: 'House number', ml: 'വീട്ടു നമ്പർ', helpEn: 'Optional. Keep the number from your book; leave empty and Rabita One gives the next number in the ward.', helpMl: 'ഐച്ഛികം. ബുക്കിലെ നമ്പർ നിലനിർത്താം; ഒഴിച്ചിട്ടാൽ വാർഡിലെ അടുത്ത നമ്പർ നൽകും.' },
  { key: 'wardCode', en: 'Ward code', ml: 'വാർഡ് കോഡ്', helpEn: 'Required. A ward code that already exists (see the Wards sheet).', helpMl: 'നിർബന്ധം. നിലവിലുള്ള വാർഡ് കോഡ് (വാർഡുകൾ ഷീറ്റ് കാണുക).' },
  { key: 'houseNameEn', en: 'House name (English)', ml: 'വീട്ടുപേര് (ഇംഗ്ലീഷ്)', helpEn: 'Required on the first row of each house.', helpMl: 'ഓരോ വീടിന്റെയും ആദ്യ വരിയിൽ നിർബന്ധം.' },
  { key: 'houseNameMl', en: 'House name (Malayalam)', ml: 'വീട്ടുപേര് (മലയാളം)', helpEn: 'Optional.', helpMl: 'ഐച്ഛികം.' },
  { key: 'ownership', en: 'Ownership', ml: 'ഉടമസ്ഥത', helpEn: 'OWN or RENTED, or empty.', helpMl: 'OWN അല്ലെങ്കിൽ RENTED, അല്ലെങ്കിൽ ഒഴിച്ചിടുക.' },
  { key: 'ratePlan', en: 'Varisankhya plan', ml: 'വരിസംഖ്യ പ്ലാൻ', helpEn: 'STANDARD, NRI or EXEMPT. Empty means STANDARD.', helpMl: 'STANDARD, NRI അല്ലെങ്കിൽ EXEMPT. ഒഴിച്ചിട്ടാൽ STANDARD.' },
  { key: 'monthlyRateInr', en: 'Monthly amount (₹)', ml: 'മാസ തുക (₹)', helpEn: 'Whole rupees. Empty means 0.', helpMl: 'മുഴുവൻ രൂപ. ഒഴിച്ചിട്ടാൽ 0.' },
  { key: 'nameEn', en: 'Member name (English)', ml: 'അംഗത്തിന്റെ പേര് (ഇംഗ്ലീഷ്)', helpEn: 'Required.', helpMl: 'നിർബന്ധം.' },
  { key: 'nameMl', en: 'Member name (Malayalam)', ml: 'അംഗത്തിന്റെ പേര് (മലയാളം)', helpEn: 'Optional.', helpMl: 'ഐച്ഛികം.' },
  { key: 'relation', en: 'Relation', ml: 'ബന്ധം', helpEn: 'HEAD, WIFE, HUSBAND, SON, DAUGHTER, FATHER, MOTHER or OTHER. Exactly one HEAD per house.', helpMl: 'HEAD, WIFE, HUSBAND, SON, DAUGHTER, FATHER, MOTHER അല്ലെങ്കിൽ OTHER. ഓരോ വീട്ടിലും ഒരു HEAD മാത്രം.' },
  { key: 'birthYear', en: 'Year of birth', ml: 'ജനന വർഷം', helpEn: 'Optional, e.g. 1975.', helpMl: 'ഐച്ഛികം, ഉദാ. 1975.' },
  { key: 'gender', en: 'Gender', ml: 'ലിംഗം', helpEn: 'M or F, or empty.', helpMl: 'M അല്ലെങ്കിൽ F, അല്ലെങ്കിൽ ഒഴിച്ചിടുക.' },
  { key: 'residence', en: 'Lives', ml: 'താമസം', helpEn: 'RESIDENT, OTHER_STATE or ABROAD. Empty means RESIDENT.', helpMl: 'RESIDENT, OTHER_STATE അല്ലെങ്കിൽ ABROAD. ഒഴിച്ചിട്ടാൽ RESIDENT.' },
  { key: 'country', en: 'Country / state', ml: 'രാജ്യം / സംസ്ഥാനം', helpEn: 'For members living away.', helpMl: 'പുറത്ത് താമസിക്കുന്നവർക്ക്.' },
  { key: 'city', en: 'City', ml: 'നഗരം', helpEn: 'For members living away.', helpMl: 'പുറത്ത് താമസിക്കുന്നവർക്ക്.' },
  { key: 'work', en: 'Work', ml: 'ജോലി', helpEn: 'Optional.', helpMl: 'ഐച്ഛികം.' },
  { key: 'phone', en: 'Mobile', ml: 'മൊബൈൽ', helpEn: 'Indian mobiles without +91 are fine; numbers abroad need the country code.', helpMl: '+91 ഇല്ലാതെ ഇന്ത്യൻ മൊബൈൽ നൽകാം; വിദേശ നമ്പറുകൾക്ക് രാജ്യ കോഡ് വേണം.' },
] as const satisfies readonly SheetColumn[];

export type RegisterColumnKey = (typeof REGISTER_COLUMNS)[number]['key'];

/** Extra columns only the export carries. An import skips them, so an exported sheet can be edited and checked again. */
export const EXPORT_ONLY_COLUMNS = [
  { key: 'houseStatus', en: 'House status', ml: 'വീടിന്റെ നില' },
  { key: 'memberStatus', en: 'Member status', ml: 'അംഗത്തിന്റെ നില' },
  { key: 'consent', en: 'Consent', ml: 'സമ്മതം' },
] as const;

/** The header cell text: English, then Malayalam. Either half alone is also accepted on import. */
export const headerText = (c: { en: string; ml: string }) => `${c.en} · ${c.ml}`;

/** Which column a header cell names, by its English or Malayalam half (case and spacing ignored). */
export function columnForHeader(cell: string): RegisterColumnKey | 'ignore' | null {
  const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
  const parts = cell.split('·').map(norm).filter(Boolean);
  if (!parts.length) return null;
  for (const c of REGISTER_COLUMNS) if (parts.some((p) => p === norm(c.en) || p === norm(c.ml))) return c.key;
  for (const c of EXPORT_ONLY_COLUMNS) if (parts.some((p) => p === norm(c.en) || p === norm(c.ml))) return 'ignore';
  return null;
}

export interface ImportProblem {
  /** Spreadsheet row number as Excel shows it (header is row 1). 0 means the whole file. */
  row: number;
  column: RegisterColumnKey | null;
  message: string;
}

export interface ImportResult {
  mode: 'check' | 'apply';
  houses: number;
  members: number;
  /** Problems stop the import; nothing is written while any remain. */
  problems: ImportProblem[];
  problemCount: number;
  /** Houses that look like ones already in the register (same ward, house name and head). Not blocking. */
  possibleDuplicates: { row: number; houseKey: string; existingHouseNo: string }[];
  applied: boolean;
}

// ── Data health (REG-16) ──

export const HEALTH_CHECKS = ['NO_HEAD', 'YOUNG_HEAD', 'NO_PHONE', 'AWAY_NO_PLACE', 'DUPLICATE_MEMBER', 'DUPLICATE_HOUSE', 'NO_BIRTH_YEAR'] as const;
export type HealthCheck = (typeof HEALTH_CHECKS)[number];

export interface HealthItem {
  houseId: string;
  houseNo: string;
  houseName: string;
  /** The member the problem is about, when it is about one. */
  memberName: string | null;
}

export interface HealthResult {
  check: HealthCheck;
  /** 'fix' blocks clean records (register rules); 'review' is worth a look. */
  level: 'fix' | 'review';
  count: number;
  /** At most HEALTH_LIST_LIMIT of them. */
  items: HealthItem[];
}

export const HEALTH_LIST_LIMIT = 50;
